import type { AbsoluteViewportRestriction } from "@map/mapiq-map/AbsoluteViewportRestriction";
import type { EventTarget } from "@map/mapiq-map/EventTarget";
import { trackError } from "@lib/infrastructure";
import {
  createEmptyHaNMapLayer,
  getHaNMapNodeId,
  getHaNMapState,
  type HaNMapLayer,
  HaNViewStoreContext,
  type HereAndNowTab,
  resolveTapTarget,
  selectHoveredNodeId,
  selectSelection,
  useHaNView,
} from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { MapiqMap } from "@molecules";

import { HaNContext } from "../shell/HaNContext";
import { type HaNLayoutMode, useHaNLayoutMode } from "../state/layout/useHaNLayoutMode";

// Keeps focused nodes clear of the filter bar, the phone carousel and the wide detail panel
const VIEWPORT_BY_LAYOUT: Record<HaNLayoutMode, AbsoluteViewportRestriction> = {
  mobile: { paddingTopPixels: 80, paddingRightPixels: 0, paddingBottomPixels: 100, paddingLeftPixels: 0 },
  medium: { paddingTopPixels: 80, paddingRightPixels: 0, paddingBottomPixels: 0, paddingLeftPixels: 0 },
  wide: { paddingTopPixels: 80, paddingRightPixels: 0, paddingBottomPixels: 0, paddingLeftPixels: 420 },
};

/** A tab's layer, with the tab it belongs to, so the map shows that tab's selection with it. */
export type HaNTabMapLayer = { tab: HereAndNowTab; layer: HaNMapLayer };

/**
 * The page's one map. It stays while tabs switch, only swapping the layer, so the floor isn't rebuilt and the view
 * is kept. Draws the layer's tab selection and the hovered node.
 */
export const HaNMapFrame = ({ floorId, tabLayer }: { floorId: string; tabLayer: HaNTabMapLayer | null }) => {
  const { tab: currentTab, building, mapViewApiRef } = useRequiredContext(HaNContext);

  // Right after a floor change the previous floor's layer is still here, and the map throws on nodes it doesn't have
  const isLayerForFloor = tabLayer?.layer.floorId === floorId;
  const tab = isLayerForFloor ? tabLayer.tab : currentTab;
  const layer = isLayerForFloor ? tabLayer.layer : createEmptyHaNMapLayer(floorId);

  const viewStore = useRequiredContext(HaNViewStoreContext);
  const layoutMode = useHaNLayoutMode();
  const selection = useHaNView(selectSelection(tab));
  const hoveredNodeId = useHaNView(selectHoveredNodeId);

  const selectedNodeId = getHaNMapNodeId(layer, selection?.id ?? null);

  const handleTap = (target: EventTarget) => {
    const tap = resolveTapTarget({ tab, target, nodeIds: layer.nodeIds, selectedNodeId });

    if (tap.type === "ignore") return;

    viewStore.selectFromMap(tab, tap.type === "select" ? tap.selection : null, {
      isPhoneLayout: layoutMode === "mobile",
    });

    // Pans the selection into view when it's behind the filter bar or the panel that the tap opens
    if (tap.type === "select") {
      mapViewApiRef.current?.fitBuildingNodes({
        nodeIds: [tap.selection.id],
        viewportRestriction: VIEWPORT_BY_LAYOUT[layoutMode],
        abortIfAlreadyInView: true,
      });
    }
  };

  // A desk spotlights its area
  const handleHover = (target: EventTarget) => {
    viewStore.hover(target.areaId ?? target.roomId ?? target.facilityId);
  };

  if (!building.buildingId) return null;

  return (
    <div className="absolute inset-0">
      <MapiqMap
        buildingId={building.buildingId}
        floorId={floorId}
        type="preference"
        mapState={getHaNMapState(layer, selectedNodeId)}
        mapStyle={layer.mapStyle}
        markerData={layer.markerData}
        spotlightedNodeId={getHaNMapNodeId(layer, hoveredNodeId)}
        mapViewApiRef={mapViewApiRef}
        onTap={handleTap}
        onHover={handleHover}
        onError={error => trackError(error, { area: "HereAndNow_Map" })}
      />
    </div>
  );
};
